import React from 'react';
import { motion } from 'framer-motion';
import {
    Code, BookOpen, Users, Award, Laptop,
    Terminal, ArrowRight, ChevronLeft, Zap,
    Smartphone, Database, Layout, Globe
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const CodingCourses = () => {
    const courseTracks = [
        { title: "Full Stack Web", desc: "Master React, Next.js, Node.js and modern databases.", icon: Globe },
        { title: "Mobile Apps", desc: "Build native iOS & Android apps using Flutter or RN.", icon: Smartphone },
        { title: "UI/UX Design", desc: "Learn Figma, prototyping, and human-centric design.", icon: Layout },
        { title: "Data Science", desc: "Python, SQL, and machine learning fundamentals.", icon: Database },
        { title: "Backend Systems", desc: "Cloud architecture, APIs, and microservices.", icon: Terminal },
        { title: "Career Prep", desc: "Interview coaching and portfolio building.", icon: Award }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="Coding Courses | Master Web & App Development - MehareTech"
                description="Accelerate your tech career with project-based coding courses. Learn React, Node.js, Flutter, and UI/UX Design from industry-leading mentors at MehareTech."
                keywords="coding courses online, web development bootcamp, mobile app development training, learn UI UX design, full stack developer course, tech career training India"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-yellow-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-yellow-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-yellow-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Skill Acceleration
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            UPGRADE YOUR <span className="bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 bg-clip-text text-transparent">CAREER GEAR</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            Join our professional courses and master the technologies that power the future.
                        </p>
                        <button className="px-8 py-3 bg-yellow-600 text-black font-bold rounded-xl hover:bg-yellow-700 transition-all flex items-center gap-2 mx-auto text-sm">
                            Explore Courses <ArrowRight size={18} />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Tracks Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {courseTracks.map((track, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-yellow-500/30 transition-all group"
                            >
                                <track.icon size={26} className="text-yellow-500 mb-4 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-bold mb-1">{track.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{track.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Info Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 tracking-tight">
                                Learn by <span className="text-yellow-500">Building</span>
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/5 rounded-xl">
                                    <BookOpen className="text-yellow-500" size={24} />
                                    <div>
                                        <h4 className="font-bold text-sm">Project-Based Learning</h4>
                                        <p className="text-[10px] text-slate-400">Build real-world apps while you learn concepts.</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/5 rounded-xl">
                                    <Users className="text-yellow-500" size={24} />
                                    <div>
                                        <h4 className="font-bold text-sm">Expert Mentorship</h4>
                                        <p className="text-[10px] text-slate-400">Get guidance from industry-leading developers.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2">
                            <img
                                src="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg"
                                alt="Coding School"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover border border-white/10"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CodingCourses;
